import {get} from 'object-path-immutable'
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import styled from 'styled-components'
import SVGInfo from '../../../../../assets/icons/information-small.svg'
import {
  inferProperties,
  MappingResult
} from '../../../../../common/integrations/payload-mapper'
import {
  ContextMapping,
  GenericMapper,
  GenericNestedMapper,
  Getter,
  Json
} from '../../../../../common/types'
import {JSONField} from '../../../../../components/library/json-field'
import {Toggle} from '../../../../../components/library/toggles/toggle'
import {Button, ButtonGroup} from '../../../components/library/button'
import {fontXSmall} from '../../../components/library/css'
import {ScriptEditor} from '../../../components/library/input/script-input'
import {
  MappedData,
  UIMappingType,
  useMappingSwitch
} from '../hooks/use-mapping-switch'
import {ObjectPropertyEditor} from './wrapped-object-input'

const Form = styled.form`
  height: 95%;
  display: flex;
  flex-direction: column;
  gap: 17px;
`

const Grid = styled.div`
  flex-grow: 1;
  min-height: 400px;
  display: grid;
  grid-template-areas:
    'mapping-header output-header'
    'mapping output';
  grid-template-rows: 42px 1fr;
  grid-template-columns: 1fr 1fr;

  &.showInput {
    grid-template-areas:
      'input-header mapping-header output-header'
      'input mapping output';
    grid-template-rows: 42px 1fr;
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const Cell = styled.div`
  border: solid 1px ${({theme}) => theme.colors.grey6};
  border-right: none;
`

const BodyCell = styled(Cell)`
  padding-top: 8px;
`

const Input = styled(BodyCell)`
  display: flex;
  flex-direction: column;
  grid-area: input;
`

const RefreshInput = styled.div`
  display: flex;
  gap: 8px;
`

const Mapping = styled(BodyCell)`
  display: flex;
  flex-direction: column;
  grid-area: mapping;
  overflow: auto;
`
const PathMapping = styled.div`
  padding: 2px 18px;
  min-height: 300px;
`

const Preview = styled(BodyCell)`
  display: flex;
  flex-direction: column;
  grid-area: output;
  border-right: solid 1px ${({theme}) => theme.colors.grey6};
  height: 100%;
`

const Header = styled(Cell)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 15px;
  padding-right: 7px;
  border-bottom: none;
  > span {
    ${fontXSmall}
    font-weight: 700;
    margin-right: 10px;
  }
`

const PassThrough = styled.p`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  color: var(--color-gray-11);
  padding: 12px 20px;
  ${fontXSmall}
  > svg {
    flex-shrink: 0;
  }
`

const Console = styled.div`
  border-top: solid 1px ${({theme}) => theme.colors.grey6};
  padding: 14px 20px;
  height: 25%;
  overflow: auto;
  ${fontXSmall}
`

const ConsoleHeader = styled.div`
  color: var(--color-grey-11);
  font-weight: 700;
`

const CodeOutput = styled(ScriptEditor)`
  flex-grow: 1;
`

const Intro = styled.p`
  margin: 0;
  margin-top: -15px;
  padding: 0;
  ${fontXSmall}
`
const Warnings = styled.p`
  border-radius: 4px;
  color: var(--color-orange-11);
`
const Errors = styled.p`
  border-radius: 4px;
  color: var(--color-orange-11);
`

const LoadSample = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 20px;
`

export type Mapper =
  | Getter // flat
  | GenericMapper // one level deep
  | GenericNestedMapper // any level deep, in practice this is CardFlowPayloadMapper

type BaseMappingEditorProps = {
  intro?: ReactNode
  mapping?: Mapper
  onChangeMapping: (mapping: Mapper, type: UIMappingType) => void
  input: Record<PropertyKey, unknown>
  onChangeInput?: (input: Record<PropertyKey, unknown>) => void
  onSavedChanges?: (mapping: Mapper, type: UIMappingType) => void
  readOnly?: boolean
  defaultPassthroughMapping?: Getter
  // Allows caller to trigger a refresh of the input
  refreshInput?: () => Promise<void>
  cancelRefreshInput?: () => Promise<void>
  waitingForInput?: boolean
  // Allows caller to perform extra validation on the mapped data
  onMappedData?: (data: unknown) => MappingResult<Record<string, Json>>
  // Allows caller to render a custom path editor, we use this to render card flow payload mapper
  renderPathEditor?: (props: {
    mapping: GenericMapper | GenericNestedMapper
    onChange: (mapping: GenericMapper | GenericNestedMapper) => void
    pathOptions: string[]
    context: Record<PropertyKey, unknown>
    readOnly?: boolean
  }) => ReactNode
  defaultScript?: string
  disablePassthrough?: boolean
  loadSampleScript?: () => string
}

type ResponseMappingEditorProps = BaseMappingEditorProps & {
  mode: 'response'
}

type RequestMappingEditorProps = BaseMappingEditorProps & {
  mode: 'request'
  stepId?: string
  contextMapping?: ContextMapping
}

type MappingEditorProps = ResponseMappingEditorProps | RequestMappingEditorProps

// note this is quite intentionally ensuring that our default for this
// component is nestedMapping. {} meets the GenericMapperCodec
const defaultMapping = {}

export const MappingEditor: FC<MappingEditorProps> = ({
  intro,
  mapping,
  onChangeMapping,
  input,
  onChangeInput,
  readOnly,
  onSavedChanges,
  defaultPassthroughMapping,
  refreshInput,
  cancelRefreshInput,
  waitingForInput,
  onMappedData,
  renderPathEditor,
  defaultScript,
  mode,
  loadSampleScript,
  disablePassthrough,
  ...props
}) => {
  const [inputError, setInputError] = useState<string>()
  const {type, setType, value, setValue, mapData} = useMappingSwitch(
    mapping ?? defaultMapping,
    {
      defaultScript,
      passThroughMapping: defaultPassthroughMapping
    }
  )
  const stepId = 'stepId' in props ? props.stepId : undefined
  const contextMapping =
    'contextMapping' in props ? props.contextMapping : undefined

  const onMappedDataRef = useRef(onMappedData)

  // Ensure that we don't allow passthrough if it's disabled
  useEffect(() => {
    if (disablePassthrough && type === 'passthrough') {
      setType('nested')
    }
  }, [type, disablePassthrough, setType])

  // Update the ref whenever onMappedData changes
  useEffect(() => {
    onMappedDataRef.current = onMappedData
  }, [onMappedData])

  const mapAndValidateData = useCallback(
    async (context: Record<PropertyKey, unknown>) => {
      const mapped = await mapData({
        context,
        contextMapping
      })

      if (mapped.valid && onMappedDataRef.current) {
        return onMappedDataRef.current(mapped.result)
      }
      return mapped
    },
    [mapData, contextMapping]
  )

  // JSON script editor wants to read and update a string
  const [inputString, setInputString] = useState(() =>
    JSON.stringify(input, null, 2)
  )

  useEffect(() => {
    setInputString(JSON.stringify(input, null, 2))
  }, [input])

  // Restore an object from the edited JSON
  // We need the object representation to pass as context to the script editor
  const context = useMemo(() => {
    setInputError(undefined)
    try {
      return JSON.parse(inputString)
    } catch {
      setInputError('Invalid input')
      return undefined
    }
  }, [inputString])

  const pathOptions = useMemo(
    () => (context && type === 'nested' ? inferProperties(context) : []),
    [context, type]
  )

  const [mapped, setMapped] = useState<MappedData>()

  // This is the preview output based on the mapping and current context
  useEffect(() => {
    mapAndValidateData(context).then(setMapped)
  }, [mapAndValidateData, context])

  // JSON script editor for preview wants to read a string
  const stringMapped = useMemo(
    () =>
      mapped && mapped.valid ? JSON.stringify(mapped.result, null, 2) : '',
    [mapped]
  )

  const displayConsole =
    (mapped && (mapped.warnings?.length || !mapped.valid)) || !!inputError

  const showInput = mode === 'response' || type !== 'nested'

  return (
    <Form
      onSubmit={e => {
        e.preventDefault()
        if (onChangeInput) {
          try {
            onChangeInput?.(context)
          } catch {
            console.error('Invalid Input')
          }
        }
        onChangeMapping(value[type], type)
        onSavedChanges?.(value[type], type)
      }}
    >
      {intro ? <Intro>{intro}</Intro> : null}
      <Grid className={showInput ? 'showInput' : ''} key={type}>
        {showInput ? (
          <>
            <Header
              style={{gridArea: 'input-header', borderTopLeftRadius: '10px'}}
            >
              <span>{mode === 'response' ? 'Sample response' : 'Input'}</span>
              {refreshInput ? (
                <RefreshInput>
                  {waitingForInput && cancelRefreshInput ? (
                    <Button
                      appearance="secondary"
                      type="button"
                      onClick={() => {
                        cancelRefreshInput()
                      }}
                    >
                      Cancel
                    </Button>
                  ) : null}
                  <Button
                    appearance="secondary"
                    type="button"
                    disabled={!!waitingForInput || readOnly}
                    onClick={() => {
                      refreshInput()
                    }}
                  >
                    {waitingForInput
                      ? 'Waiting...'
                      : inputString
                      ? 'Capture sample'
                      : 'Capture new sample'}
                  </Button>
                </RefreshInput>
              ) : null}
            </Header>
            <Input>
              <ScriptEditor
                readOnly={onChangeInput ? readOnly : true}
                height="100%"
                language="json"
                value={inputString}
                onChange={onChangeInput ? setInputString : undefined}
              />
            </Input>
          </>
        ) : null}

        <Header style={{gridArea: 'mapping-header'}}>
          <span>Mapping</span>
          <Toggle<UIMappingType>
            value={type}
            style={{minWidth: '350px'}}
            options={[
              ...(disablePassthrough
                ? []
                : [
                    {
                      value: 'passthrough' as UIMappingType,
                      text: 'Pass-through'
                    }
                  ]),
              {value: 'script', text: 'JavaScript'},
              {value: 'nested', text: 'Basic'}
            ]}
            onChange={t => setType(t)}
            readOnly={readOnly}
          />
        </Header>

        <Mapping>
          {type === 'script' ? (
            <>
              <ScriptEditor
                context={context}
                value={get(value, 'script.script', '')}
                onChange={script => setValue({type: 'script', script})}
                readOnly={readOnly}
                lineNumbers={true}
                height="100%"
              />
              {loadSampleScript ? (
                <LoadSample>
                  <Button
                    appearance="secondary"
                    type="button"
                    onClick={() =>
                      setValue({type: 'script', script: loadSampleScript()})
                    }
                  >
                    Load sample script
                  </Button>
                </LoadSample>
              ) : null}
            </>
          ) : type === 'nested' ? (
            <PathMapping>
              {renderPathEditor ? (
                renderPathEditor({
                  mapping: value[type],
                  onChange: setValue,
                  pathOptions,
                  context,
                  readOnly
                })
              ) : (
                <ObjectPropertyEditor
                  context={context}
                  data={value[type] as GenericMapper}
                  onChange={setValue}
                  pathOptions={pathOptions}
                  readOnly={readOnly}
                  scriptTheme="light"
                  stepId={stepId}
                  contextMapping={contextMapping}
                />
              )}
            </PathMapping>
          ) : (
            <PassThrough>
              <SVGInfo />
              The pass-through option sets the output to exactly the same value
              as the input
            </PassThrough>
          )}
        </Mapping>

        <Header
          style={{
            gridArea: 'output-header',
            borderTopRightRadius: '10px',
            borderRight: 'solid 1px var(--color-grey-6)'
          }}
        >
          <span>Output</span>
        </Header>
        <Preview>
          <CodeOutput
            language="json"
            value={stringMapped}
            readOnly={true}
            onChange={() => undefined} // readonly
          />
          <Console
            style={{
              display: displayConsole ? 'block' : 'none'
            }}
          >
            <ConsoleHeader>Console</ConsoleHeader>
            {mapped && mapped.warnings && (
              <Warnings>{mapped.warnings.join(', ')}</Warnings>
            )}
            {mapped && !mapped.valid && (
              <Errors>{mapped.errors.join(', ')}</Errors>
            )}
            {inputError ? <Errors>{inputError}</Errors> : null}
          </Console>
        </Preview>
      </Grid>
      <ButtonGroup>
        <Button
          size="large"
          disabled={(mapped && !mapped.valid) || readOnly}
          type="submit"
        >
          Save changes
        </Button>
      </ButtonGroup>
    </Form>
  )
}

export const MappingPreview: FC<{
  mapping?: GenericMapper | Getter
  context: Record<PropertyKey, unknown>
  contextMapping: ContextMapping
  label?: string
}> = ({mapping, context, label, contextMapping}) => {
  const {mapData} = useMappingSwitch(mapping ?? defaultMapping)
  const [mapped, setMapped] = useState<MappedData>()
  useEffect(() => {
    mapData({context, contextMapping}).then(setMapped)
  }, [mapData, context, contextMapping, mapping])

  const value = useMemo(
    () => (mapped && mapped.valid ? mapped.result : {}),
    [mapped]
  )

  return (
    <JSONField
      raw={true}
      label={label ?? 'Mapped Preview'}
      value={value}
      onChange={() => undefined}
      readOnly={true}
    />
  )
}
